import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Grid, GridType, isInaccessibleStorable, MinimalContainer} from 'constants/SampleConstants';
import {FillDirection} from 'lookup-config/LookupConfigModalConstants';
import notifications from 'notifications';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {getGridTypeName} from 'samples/grids/GridUtil';
import ScanToMoveGridHeader from 'samples/grids/ScanToMoveGridHeader';
import ScanToMoveGridTable, {
  useInitialContainersByCoordinatesMapping,
} from 'samples/grids/ScanToMoveGridTable';
import {ScanToMoveCellByCoordinates} from 'samples/grids/ScanToMoveGridUtil';
import {setScanModeCells, setScanningCoordinates} from 'samples/GridUIReduxActions';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {searchStorablesWithSummaries} from 'samples/StorableActions';
import {FilterKey, Operator, SearchParams} from 'search/SearchConstants';
import useOnMount from 'ui-library/hooks/useOnMount';
import {Deferred} from 'util/makeDeferred';

export interface ScanToMoveGridTabProps {
  fillDirection: FillDirection;
  setCancelPromiseKey: (key: Deferred<string>) => void;
  exitScanMode: () => void;
  grid: Grid;
  gridType: GridType;
}

export default function ScanToMoveGridTab(props: ScanToMoveGridTabProps): JSX.Element {
  const {
    containersByCoordinates: initialContainersByCoordinates,
    orderedAvailableCoordinates: orderedInitialAvailableCoordinates,
  } = useInitialContainersByCoordinatesMapping(props.grid.cells, props.fillDirection);
  const scanModeCells = useSelector(
    (state: BenchlingState) =>
      state.ui.samples.gridUI[props.grid.id]?.scanModeCells ?? initialContainersByCoordinates
  );
  const [containersByCoordinates, setContainersByCoordinates] =
    useState<ScanToMoveCellByCoordinates>(scanModeCells);
  const [isProcessingScan, setIsProcessingScan] = useState(false);

  const dispatch = useDispatch<BenchlingDispatch>();
  const {
    grid: {id: gridId},
  } = props;
  const scanningCoordinates = useSelector(
    (state: BenchlingState) => state.ui.samples.gridUI[gridId]?.scanningCoordinates
  );

  function handleSetScanningCoordinates(coordinates: string): void {
    dispatch(setScanningCoordinates(gridId, coordinates));
  }

  function handleExitScanMode(): void {
    dispatch(setScanModeCells(gridId, null));
    props.exitScanMode();
  }

  // Initialize the scanning coordinates once when this first renders.
  useOnMount(() => {
    if (scanningCoordinates == null) {
      dispatch(setScanningCoordinates(gridId, orderedInitialAvailableCoordinates[0]));
    }
    if (containersByCoordinates != null) {
      dispatch(setScanModeCells(gridId, containersByCoordinates));
    }
  });

  // Update scanning coordinates to next available, used after scanning an item
  function updateScanningCoordinatesToNextAvailable(scannedGridPosition: string): void {
    const currentScanIndex = orderedInitialAvailableCoordinates.findIndex(
      (coord) => coord === scannedGridPosition
    );
    const nextScanIndex =
      currentScanIndex +
      1 +
      orderedInitialAvailableCoordinates
        .slice(currentScanIndex + 1)
        .findIndex((coord) => containersByCoordinates[coord].container == null);
    const nextScanCoordinates = orderedInitialAvailableCoordinates[nextScanIndex];
    handleSetScanningCoordinates(nextScanCoordinates);
  }

  function handleScan(scannedGridPosition: string, container: MinimalContainer): void {
    const newContainerMapping = {
      [scannedGridPosition]: {
        ...containersByCoordinates[scannedGridPosition],
        container,
        isPreexisting: false,
      },
    };
    handleUpdateContainersAtCoordinates(newContainerMapping);
    updateScanningCoordinatesToNextAvailable(scannedGridPosition);
  }

  function updateExistingContainer(barcode: string): boolean {
    const existingContainerByCoordinate = Object.entries(containersByCoordinates).filter(
      ([_, {container}]) =>
        container != null &&
        !isInaccessibleStorable(container) &&
        container.barcode?.toLowerCase() === barcode.toLowerCase()
    );
    if (existingContainerByCoordinate.length > 0) {
      const [coordinate, scanToMoveGridCell] = existingContainerByCoordinate[0];
      const modifiedContainerMapping = {
        // move the existing container to the new location
        [scanningCoordinates!]: {
          ...containersByCoordinates[scanningCoordinates!],
          container: scanToMoveGridCell.container,
          isPreexisting: scanToMoveGridCell.isPreexisting,
          originalGridCoordinates:
            scanToMoveGridCell.originalGridCoordinates ||
            (scanToMoveGridCell.isPreexisting ? coordinate : null),
        },
        // remove the container from the old location
        [coordinate]: {
          ...scanToMoveGridCell,
          container: null,
          isPreexisting: false,
          originalGridCoordinates: null,
        },
      };
      handleUpdateContainersAtCoordinates(modifiedContainerMapping);
      updateScanningCoordinatesToNextAvailable(scanningCoordinates!);
      return true;
    }
    return false;
  }

  async function handleBarcodeSubmit(barcode: string, onFinish: () => void): Promise<void> {
    setIsProcessingScan(true);
    try {
      if (updateExistingContainer(barcode)) {
        return;
      }

      const searchQuery: SearchParams = {
        offset: 0,
        limit: 1,
        query: '',
        sorts: [],
        filters: [
          {key: FilterKey.TYPES, operator: Operator.IS_ONE_OF, value: [ModelTypeConstants.CONTAINER]},
          {key: FilterKey.BARCODES, value: [barcode], operator: Operator.IS_ONE_OF},
        ],
      };
      const {responsePromise, cancelPromise} = searchStorablesWithSummaries(searchQuery);
      props.setCancelPromiseKey(cancelPromise);
      const searchResults = await responsePromise;
      if (searchResults.error) {
        notifications.error(searchResults.error);
        return;
      }

      if (searchResults.storableSummaries.length > 0) {
        const storableSummary = searchResults.storableSummaries[0];

        // Check if the container schema is accepted in the grid
        const acceptedSchemaId = props.grid.storableSchema.containerSchemaId;
        const acceptedContainerSchema = SampleSchemaStore.getContainerSchema(acceptedSchemaId);
        if (acceptedSchemaId != null && storableSummary.storableSchema.id !== acceptedSchemaId) {
          const gridTypeName = getGridTypeName(gridId);
          notifications.error(
            `The container schema "${
              storableSummary.storableSchema.name
            }" is not accepted in this ${gridTypeName}. This ${gridTypeName} only accepts "${
              acceptedContainerSchema!.name
            }". Please contact a site admin to modify ${gridTypeName} permissions.`
          );
          return;
        }

        const containedItems =
          searchResults.storableSummaryExtras.containedItemsByContainerId?.[storableSummary.id] ?? [];
        const contents = containedItems.map(({id}) => ({containedItemId: id}));
        const containerInfo = {...storableSummary, contents};
        handleScan(scanningCoordinates!, containerInfo);
      } else {
        notifications.error(
          `Container "${barcode}" not found or you may not have permissions to view this item.`
        );
      }
    } finally {
      setIsProcessingScan(false);
      onFinish();
    }
  }

  function handleUpdateContainersAtCoordinates(modifiedContainers: ScanToMoveCellByCoordinates): void {
    const newContainersByCoordinates = {...containersByCoordinates, ...modifiedContainers};
    setContainersByCoordinates(newContainersByCoordinates);
    dispatch(setScanModeCells(gridId, newContainersByCoordinates));
  }

  const gridDimensions = {width: props.grid.cells[0].length, height: props.grid.cells.length};

  return (
    <>
      <ScanToMoveGridHeader
        isProcessingScan={isProcessingScan}
        fillDirection={props.fillDirection}
        onCancel={handleExitScanMode}
        onSubmit={handleExitScanMode}
        scanCoordinates={scanningCoordinates ?? ''}
        onChangeScanCoordinates={handleSetScanningCoordinates}
        onBarcodeSubmit={handleBarcodeSubmit}
        containersByCoordinates={containersByCoordinates}
        gridId={gridId}
        gridDimensions={gridDimensions}
        gridType={props.gridType}
      />
      <ScanToMoveGridTable
        containersByCoordinates={containersByCoordinates}
        updateContainersForCoordinates={handleUpdateContainersAtCoordinates}
        gridDimensions={gridDimensions}
        gridType={props.gridType}
        selectedCoordinates={scanningCoordinates}
        setSelectedCoordinates={handleSetScanningCoordinates}
      />
    </>
  );
}
